import axios from "axios";

import config from "data/config";
import { getToken, removeToken } from "./utils/localStorage";
import mapCodeToErrorText from "./error-mapping";

const baseURL = config.server;

const instance = axios.create({
  baseURL: baseURL,
  timeout: 30000, // 30 seconds timeout
});

export const authInstance = axios.create({
  baseURL: baseURL,
  timeout: 30000, // 30 seconds timeout
});

// Request interceptor for main instance
instance.interceptors.request.use(
  (config) => {
    const configInstance = { ...config };
    const token = getToken();
    
    // Add authorization header if token exists
    if (token && config.headers) {
      config.headers.Authorization = `${token}`;
    }
    
    return configInstance;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for main instance
instance.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    }
    return response;
  },
  (error) => {
    const { response, request, config } = error;

    // Create enhanced error object
    const enhancedError = {
      ...error,
      timestamp: new Date().toISOString(),
      url: config?.url,
      method: config?.method?.toUpperCase(),
    };

    // Network error (no response received)
    if (!response && request) {
      console.error('❌ Network Error:', {
        message: 'No response received from server',
        url: config?.url,
        method: config?.method?.toUpperCase(),
      });
      
      enhancedError.message = 'Network error. Please check your internet connection.';
      return Promise.reject(enhancedError);
    }

    // Request timeout
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      console.error('⏱️ Timeout Error:', {
        url: config?.url,
        method: config?.method?.toUpperCase(),
        timeout: config?.timeout,
      });
      
      enhancedError.message = 'Request timeout. Please try again.';
      return Promise.reject(enhancedError);
    }

    // HTTP error responses
    if (response) {
      const { status, data } = response;
      
      console.error(`❌ HTTP ${status} Error:`, {
        url: config?.url,
        method: config?.method?.toUpperCase(),
        status,
        data: data?.message || data,
      });

      // Authentication/Authorization errors
      if (status === 401 || status === 403) {
        removeToken();
        window.location.href = "/auth/login";
        return Promise.reject(enhancedError);
      }

      // Server errors (5xx)
      if (status >= 500) {
        enhancedError.message = 'Server error. Please try again later.';
      }
      // Client errors (4xx)
      else if (status >= 400) {
        const errorMessage = data?.message || data?.error || mapCodeToErrorText(data?.code);
        enhancedError.message = errorMessage;
      }
    }

    // Request setup error
    if (!request && !response) {
      console.error('❌ Request Setup Error:', error.message);
      enhancedError.message = 'Request configuration error.';
    }

    return Promise.reject(enhancedError);
  }
);

// Request interceptor for auth instance (no authorization header)
authInstance.interceptors.request.use(
  (config) => {
    const configInstance = { ...config };
    
    return configInstance;
  },
  (error) => {
    console.error('Auth request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for auth instance (no auth redirect)
authInstance.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ AUTH ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    }
    return response;
  },
  (error) => {
    const { response, request, config } = error;

    // Create enhanced error object
    const enhancedError = {
      ...error,
      timestamp: new Date().toISOString(),
      url: config?.url,
      method: config?.method?.toUpperCase(),
    };

    // Network error (no response received)
    if (!response && request) {
      console.error('❌ Auth Network Error:', {
        message: 'No response received from server',
        url: config?.url,
        method: config?.method?.toUpperCase(),
      });
      
      enhancedError.message = 'Network error. Please check your internet connection.';
      return Promise.reject(enhancedError);
    }

    // Request timeout
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      console.error('⏱️ Auth Timeout Error:', {
        url: config?.url,
        method: config?.method?.toUpperCase(),
        timeout: config?.timeout,
      });
      
      enhancedError.message = 'Request timeout. Please try again.';
      return Promise.reject(enhancedError);
    }

    // HTTP error responses
    if (response) {
      const { status, data } = response;
      
      console.error(`❌ Auth HTTP ${status} Error:`, {
        url: config?.url,
        method: config?.method?.toUpperCase(),
        status,
        data: data?.message || data,
      });

      // Server errors (5xx)
      if (status >= 500) {
        enhancedError.message = 'Server error. Please try again later.';
      }
      // Client errors (4xx) - including auth errors but no redirect
      else if (status >= 400) {
        const errorMessage = data?.message || data?.error || mapCodeToErrorText(data?.code);
        enhancedError.message = errorMessage;
      }
    }

    // Request setup error
    if (!request && !response) {
      console.error('❌ Auth Request Setup Error:', error.message);
      enhancedError.message = 'Request configuration error.';
    }

    return Promise.reject(enhancedError);
  }
);

export default instance;
